import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, BACKEND } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { brl, dt, STATUS, PAY, maskCpf, maskPhone, useSeo } from "@/lib/fmt";
import PaymentPanel from "@/components/store/PaymentPanel";

export function CustomerLogin() {
  const { user, login, setUser } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("senha");
  const [f, setF] = useState({ email: "", senha: "", nome: "", aceite: false });
  const [msg, setMsg] = useState("");
  useSeo("Entrar");
  if (user) return <Navigate to={user.papel === "cliente" ? "/conta" : "/admin"} replace />;
  const go = async (e) => {
    e.preventDefault();
    try {
      if (tab === "senha") { const u = await login(f.email.trim(), f.senha); nav(u.papel === "cliente" ? "/conta" : "/admin"); }
      else if (tab === "magico") { const r = await api.post("/auth/magic-link", { email: f.email }); setMsg(r.data.mensagem); }
      else { const r = await api.post("/auth/register", { nome: f.nome, email: f.email, senha: f.senha, aceite_termos: f.aceite }); setUser(r.data); nav("/conta"); }
    } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <div className="container-na max-w-md pt-12">
      <h1 className="text-4xl">Sua conta</h1>
      <p className="mt-2 text-sm text-na-ink2">Clientes e equipe usam este mesmo acesso. A equipe vai direto para o painel administrativo.</p>
      <div className="mt-6 flex gap-1 rounded-full bg-na-sage2 p-1">
        {[["senha", "Entrar"], ["magico", "Link por e-mail"], ["cadastro", "Criar conta"]].map(([k, l]) => <button key={k} onClick={() => { setTab(k); setMsg(""); }} className={`flex-1 rounded-full py-2 text-xs font-semibold ${tab === k ? "bg-white shadow" : ""}`} data-testid={`login-tab-${k}`}>{l}</button>)}
      </div>
      <form onSubmit={go} className="card-na mt-4 space-y-4 p-6" data-testid="customer-login-form">
        {tab === "cadastro" && <div><label htmlFor="c-nome" className="label-na">Nome</label><input id="c-nome" className="input-na" value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} data-testid="register-name" /></div>}
        <div><label htmlFor="c-email" className="label-na">E-mail</label><input id="c-email" type="email" className="input-na" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email" /></div>
        {tab !== "magico" && <div><label htmlFor="c-senha" className="label-na">Senha</label><input id="c-senha" type="password" className="input-na" value={f.senha} onChange={(e) => setF({ ...f, senha: e.target.value })} data-testid="login-password" /></div>}
        {tab === "cadastro" && <label className="flex gap-2 text-sm"><input type="checkbox" checked={f.aceite} onChange={(e) => setF({ ...f, aceite: e.target.checked })} data-testid="register-terms" /> Aceito os termos e a política de privacidade</label>}
        {msg && <p className="rounded-2xl bg-na-sage2 p-3 text-sm" data-testid="magic-link-message">{msg}</p>}
        <button className="btn-na w-full" data-testid="login-submit">{tab === "senha" ? "Entrar" : tab === "magico" ? "Enviar link" : "Criar conta"}</button>
        {tab === "senha" && <Link to="/redefinir-senha" className="block text-center text-sm font-semibold text-na-forest underline" data-testid="forgot-password-link">Esqueci minha senha</Link>}
      </form>
    </div>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [senha2, setSenha2] = useState("");
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(false);
  useSeo("Redefinir senha");
  const go = async (e) => {
    e.preventDefault();
    try {
      if (!token) { const r = await api.post("/auth/forgot-password", { email: email.trim() }); setMsg(r.data.mensagem); return; }
      if (senha.length < 10) return toast.error("A nova senha precisa ter 10 ou mais caracteres");
      if (senha !== senha2) return toast.error("As senhas não são iguais");
      const r = await api.post("/auth/reset-password", { token, nova_senha: senha });
      setMsg(r.data.mensagem);
      setDone(true);
    } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <div className="container-na max-w-md pt-12">
      <h1 className="text-4xl">Redefinir senha</h1>
      <p className="mt-2 text-sm text-na-ink2">{token ? "Crie sua nova senha." : "Informe seu e-mail. Vale para clientes e equipe."}</p>
      <form onSubmit={go} className="card-na mt-6 space-y-4 p-6" data-testid="reset-password-form">
        {!token ? (
          <div><label htmlFor="rp-email" className="label-na">E-mail</label><input id="rp-email" type="email" required className="input-na" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" /></div>
        ) : !done && (
          <>
            <div><label htmlFor="rp-n" className="label-na">Nova senha (10+ caracteres)</label><input id="rp-n" type="password" className="input-na" value={senha} onChange={(e) => setSenha(e.target.value)} data-testid="reset-new-password" /></div>
            <div><label htmlFor="rp-c" className="label-na">Repita a nova senha</label><input id="rp-c" type="password" className="input-na" value={senha2} onChange={(e) => setSenha2(e.target.value)} data-testid="reset-confirm-password" /></div>
          </>
        )}
        {msg && <p className="rounded-2xl bg-na-sage2 p-3 text-sm" data-testid="reset-message">{msg}</p>}
        {done ? <Link to="/entrar" className="btn-na w-full" data-testid="reset-go-login">Ir para o login</Link> : <button className="btn-na w-full" data-testid="reset-submit">{token ? "Salvar nova senha" : "Enviar link"}</button>}
      </form>
    </div>
  );
}

export function MagicLogin() {
  const [sp] = useSearchParams();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [err, setErr] = useState("");
  useEffect(() => {
    api.post("/auth/magic-verify", { token: sp.get("token") }).then((r) => { setUser(r.data); nav("/conta"); }).catch((e) => setErr(errMsg(e)));
  }, [sp, setUser, nav]);
  return <div className="container-na py-20 text-center">{err || "Entrando..."}</div>;
}

const Badge = ({ s }) => <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS[s]?.cls}`}>{STATUS[s]?.label || s}</span>;

function Orders() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/account/orders").then((r) => setList(r.data)); }, []);
  if (!list) return <p>Carregando...</p>;
  if (!list.length) return <p className="text-na-ink2" data-testid="account-no-orders">Você ainda não tem pedidos.</p>;
  return (
    <ul className="space-y-3" data-testid="account-orders">
      {list.map((o) => (
        <li key={o.id}><Link to={`/conta/pedidos/${o.id}`} className="card-na flex flex-wrap items-center justify-between gap-2 p-5 hover:border-na-forest" data-testid={`account-order-${o.numero}`}>
          <span className="font-semibold">{o.numero}</span><span className="text-sm text-na-ink2">{dt(o.criado_em)}</span><Badge s={o.status} /><b>{brl(o.total)}</b>
        </Link></li>
      ))}
    </ul>
  );
}

function OrderDetail() {
  const { id } = useParams();
  const { setItems } = useStore();
  const nav = useNavigate();
  const [o, setO] = useState(null);
  const [ret, setRet] = useState({ motivo: "", descricao: "", efeito_adverso: false, open: false });
  const [rev, setRev] = useState({});
  const load = () => api.get(`/account/orders/${id}`).then((r) => setO(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (!o) return <p>Carregando...</p>;
  const repeat = async () => { const r = await api.post(`/account/orders/${id}/repeat`); setItems(r.data.itens); nav("/checkout"); };
  const second = async () => { try { await api.post(`/account/orders/${id}/second-via`); toast.success("Nova via gerada"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const sendReturn = async () => { try { await api.post(`/account/orders/${id}/return`, ret); toast.success("Solicitação enviada"); setRet({ ...ret, open: false }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const sendReview = async (pid) => { try { await api.post("/account/reviews", { pedido_id: id, produto_id: pid, nota: rev[pid]?.nota || 5, texto: rev[pid]?.texto || "" }); toast.success("Obrigado! Sua avaliação será publicada após moderação."); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-5" data-testid="account-order-detail">
      <div className="flex flex-wrap items-center gap-3"><h2 className="font-serif text-2xl">{o.numero}</h2><Badge s={o.status} /></div>
      <ol className="border-l-2 border-na-line pl-5" data-testid="order-timeline">{o.historico.map((h, i) => <li key={i} className="mb-3"><p className="text-sm font-semibold">{STATUS[h.status]?.label}</p><p className="text-xs text-na-ink2">{dt(h.em)}</p></li>)}</ol>
      {o.rastreio && <p className="text-sm">Rastreio: <a className="font-semibold underline" target="_blank" rel="noreferrer" href={`https://rastreamento.correios.com.br/app/index.php?objetos=${o.rastreio}`} data-testid="tracking-code">{o.rastreio}</a></p>}
      <ul className="card-na divide-y divide-na-line p-5">{o.itens.map((i) => (
        <li key={i.slug + i.frascos} className="py-3 text-sm">
          <div className="flex justify-between"><span>{i.quantidade}x {i.nome} ({i.frascos} un.)</span><b>{brl(i.total)}</b></div>
          {o.status === "entregue" && (
            <div className="mt-2 flex flex-wrap gap-2">
              <select aria-label="Nota" className="input-na w-20 py-1.5" value={rev[i.produto_id]?.nota || 5} onChange={(e) => setRev({ ...rev, [i.produto_id]: { ...rev[i.produto_id], nota: Number(e.target.value) } })}>{[5, 4, 3, 2, 1].map((n) => <option key={n}>{n}</option>)}</select>
              <input aria-label="Comentário" placeholder="Como foi sua experiência?" className="input-na flex-1 py-1.5" onChange={(e) => setRev({ ...rev, [i.produto_id]: { ...rev[i.produto_id], texto: e.target.value } })} data-testid={`review-text-${i.slug}`} />
              <button className="btn-line px-4 py-1.5" onClick={() => sendReview(i.produto_id)} data-testid={`review-submit-${i.slug}`}>Avaliar</button>
            </div>
          )}
        </li>))}
        <li className="flex justify-between pt-3 font-bold"><span>Total ({PAY[o.pagamento.metodo]})</span><span>{brl(o.total)}</span></li>
      </ul>
      {o.status === "aguardando_pagamento" && <div className="card-na p-5"><PaymentPanel order={o} /><button className="btn-line mt-3" onClick={second} data-testid="second-via-button">Gerar segunda via</button></div>}
      <div className="flex flex-wrap gap-2">
        <button className="btn-na" onClick={repeat} data-testid="repeat-order-button">Repetir pedido</button>
        {o.nfe && <a className="btn-line" href={`${BACKEND}/api/account/orders/${o.id}/nfe.pdf`} target="_blank" rel="noreferrer" data-testid="nfe-download">Nota fiscal (PDF)</a>}
        {["enviado", "entregue"].includes(o.status) && <button className="btn-line" onClick={() => setRet({ ...ret, open: !ret.open })} data-testid="return-request-button">Pedir troca ou devolução</button>}
      </div>
      {ret.open && (
        <div className="card-na space-y-3 p-5" data-testid="return-form">
          <label htmlFor="r-mot" className="label-na">Motivo</label>
          <select id="r-mot" className="input-na" value={ret.motivo} onChange={(e) => setRet({ ...ret, motivo: e.target.value })} data-testid="return-reason"><option value="">Escolha</option><option>Desistência (7 dias)</option><option>Produto avariado</option><option>Produto errado</option><option>Reação ao produto</option></select>
          <textarea aria-label="Descrição" className="input-na" rows={3} placeholder="Conte o que aconteceu" value={ret.descricao} onChange={(e) => setRet({ ...ret, descricao: e.target.value })} data-testid="return-description" />
          <label className="flex gap-2 text-sm"><input type="checkbox" checked={ret.efeito_adverso} onChange={(e) => setRet({ ...ret, efeito_adverso: e.target.checked })} /> Tive uma reação indesejada ao usar o produto</label>
          <button className="btn-na" disabled={!ret.motivo} onClick={sendReturn} data-testid="return-submit">Enviar solicitação</button>
        </div>
      )}
      {o.devolucoes?.map((d) => <p key={d.id} className="text-sm">Solicitação de {d.tipo}: <b>{d.status}</b> {d.codigo_reverso && `· código de postagem ${d.codigo_reverso}`}</p>)}
    </div>
  );
}

function Subs() {
  const [list, setList] = useState([]);
  const load = () => api.get("/account/subscriptions").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, a) => { await api.post(`/account/subscriptions/${id}/${a}`); toast.success("Assinatura atualizada"); load(); };
  if (!list.length) return <p className="text-na-ink2" data-testid="no-subscriptions">Você não tem assinaturas. Escolha "Assinar" na página do produto e ganhe 10% off.</p>;
  return list.map((s) => (
    <div key={s.id} className="card-na mb-3 p-5" data-testid={`subscription-${s.id}`}>
      <p className="font-semibold">{s.itens.map((i) => `${i.quantidade}x ${i.nome}`).join(", ")}</p>
      <p className="text-sm text-na-ink2">A cada {s.intervalo_dias} dias · próxima {dt(s.proxima_cobranca)} · <b>{s.status}</b></p>
      {s.status !== "cancelada" && <div className="mt-3 flex flex-wrap gap-2">
        {s.status === "pausada" ? <button className="btn-line px-4 py-2" onClick={() => act(s.id, "retomar")}>Retomar</button> : <button className="btn-line px-4 py-2" onClick={() => act(s.id, "pausar")} data-testid="sub-pause">Pausar</button>}
        <button className="btn-line px-4 py-2" onClick={() => act(s.id, "pular")} data-testid="sub-skip">Pular próxima</button>
        <button className="px-4 py-2 text-sm font-semibold text-red-700" onClick={() => act(s.id, "cancelar")} data-testid="sub-cancel">Cancelar</button>
      </div>}
    </div>
  ));
}

function Profile({ only }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/account/profile").then((r) => setP({ ...r.data, enderecos: r.data.enderecos || [] })); }, []);
  if (!p) return <p>Carregando...</p>;
  const save = async () => { try { await api.put("/account/profile", p); toast.success("Dados salvos"); } catch (e) { toast.error(errMsg(e)); } };
  const exportData = async () => { const r = await api.get("/account/export"); const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" })); a.download = "meus-dados-nativa.json"; a.click(); };
  const del = async () => { if (!window.confirm("Excluir sua conta? Seus pedidos ficam anonimizados para fins fiscais.")) return; await api.delete("/account"); await logout(); nav("/"); };
  const setE = (i, k, v) => setP({ ...p, enderecos: p.enderecos.map((e, j) => (j === i ? { ...e, [k]: v } : e)) });
  if (only === "enderecos") return (
    <div className="space-y-3" data-testid="addresses">
      {p.enderecos.map((e, i) => (
        <div key={i} className="card-na grid grid-cols-2 gap-2 p-4">
          {["cep", "rua", "numero", "complemento", "bairro", "cidade", "uf"].map((k) => <input key={k} aria-label={k} placeholder={k} className="input-na py-2" value={e[k] || ""} onChange={(ev) => setE(i, k, ev.target.value)} />)}
          <button className="text-sm text-red-700" onClick={() => setP({ ...p, enderecos: p.enderecos.filter((_, j) => j !== i) })}>Remover</button>
        </div>
      ))}
      <div className="flex gap-2"><button className="btn-line" onClick={() => setP({ ...p, enderecos: [...p.enderecos, {}] })} data-testid="add-address">Novo endereço</button><button className="btn-na" onClick={save} data-testid="save-addresses">Salvar</button></div>
    </div>
  );
  return (
    <div className="card-na space-y-4 p-6" data-testid="profile-form">
      <div><label htmlFor="p-nome" className="label-na">Nome</label><input id="p-nome" className="input-na" value={p.nome || ""} onChange={(e) => setP({ ...p, nome: e.target.value })} data-testid="profile-name" /></div>
      <p className="text-sm">E-mail: {p.email}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div><label htmlFor="p-cpf" className="label-na">CPF</label><input id="p-cpf" className="input-na" value={p.cpf || ""} onChange={(e) => setP({ ...p, cpf: maskCpf(e.target.value) })} /></div>
        <div><label htmlFor="p-cel" className="label-na">Celular</label><input id="p-cel" className="input-na" value={p.celular || ""} onChange={(e) => setP({ ...p, celular: maskPhone(e.target.value) })} /></div>
      </div>
      <label className="flex gap-2 text-sm"><input type="checkbox" checked={!!p.consentimentos?.newsletter} onChange={(e) => setP({ ...p, consentimentos: { ...p.consentimentos, newsletter: e.target.checked } })} /> Quero receber e-mails da Nativa Amazon</label>
      <div className="flex flex-wrap gap-2"><button className="btn-na" onClick={save} data-testid="profile-save">Salvar</button><button className="btn-line" onClick={exportData} data-testid="export-data">Baixar meus dados</button><button className="px-4 text-sm font-semibold text-red-700" onClick={del} data-testid="delete-account">Excluir conta</button></div>
    </div>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  useSeo("Minha conta");
  if (user === null) return <div className="container-na py-20">Carregando...</div>;
  if (!user) return <Navigate to="/entrar" replace />;
  if (user.papel !== "cliente") return <Navigate to="/admin" replace />;
  const tabs = [["pedidos", "Pedidos"], ["assinaturas", "Assinaturas"], ["enderecos", "Endereços"], ["dados", "Dados"]];
  return (
    <div className="container-na pt-10">
      <div className="flex flex-wrap items-end justify-between gap-3"><h1 className="text-4xl">Olá, {user.nome?.split(" ")[0]}</h1><button className="text-sm font-semibold text-na-ink2 underline" onClick={logout} data-testid="customer-logout">Sair</button></div>
      <nav className="no-scrollbar mt-6 flex gap-2 overflow-x-auto">{tabs.map(([k, l]) => <NavLink key={k} to={`/conta/${k}`} className={({ isActive }) => `chip ${isActive ? "chip-on" : ""}`} data-testid={`account-tab-${k}`}>{l}</NavLink>)}</nav>
      <div className="mt-6 max-w-3xl">
        <Routes>
          <Route index element={<Navigate to="pedidos" replace />} />
          <Route path="pedidos" element={<Orders />} />
          <Route path="pedidos/:id" element={<OrderDetail />} />
          <Route path="assinaturas" element={<Subs />} />
          <Route path="enderecos" element={<Profile only="enderecos" />} />
          <Route path="dados" element={<Profile />} />
        </Routes>
      </div>
    </div>
  );
}
